<template>
    <v-navigation-drawer
        v-model="openDrawer"
        app
        dark
        color="sidenav"
        :width="SIDEBAR_WIDTH"
    >
        <v-sheet
            color="sidenav"
            class="pa-2 d-flex justify-center"
        >
            <h2 class="nav-title">
                <a
                    :href="homeURL"
                    target="_blank"
                >
                    {{ $t(`navBar.navTitle`) }}
                </a>
            </h2>
        </v-sheet>

        <v-divider class="nav-divider"></v-divider>

        <v-list class="side__nav-list pt-0">
            <template v-for="(navItem, i) in navItems">
                <v-list-group
                    v-if="navItem.hasSubItems"
                    :key="`${i}-b`"
                    v-model="navItem.active"
                    :class="['side-menu-group', { 'subsystems-menu': navItem.label === 'subsystems' }]"
                >
                    <template v-slot:prependIcon>
                        <v-icon
                            class="color-icon"
                            size="18"
                        >
                            {{ navItem.icon }}
                        </v-icon>
                    </template>
                    <template v-slot:appendIcon>
                        <v-icon
                            class="color-icon"
                            size="18"
                        >
                            fa-solid fa-angle-down
                        </v-icon>
                    </template>
                    <template v-slot:activator>
                        <v-list-item-content class="position-relative">
                            <v-list-item-title class="text-wrap">
                                {{ $t(`sideBar.${navItem.label}`) }}
                            </v-list-item-title>
                            <div
                                v-if="navItem.label === 'subsystems'"
                                class="new-badge"
                            >
                                {{ $t("general.new") }}
                            </div>
                        </v-list-item-content>
                    </template>

                    <template v-for="(subItem, y) in navItem.subItems">
                        <v-list-group
                            v-if="subItem.hasInnerItems && canViewOption(subItem.canView)"
                            :key="`${y}-b`"
                            v-model="subItem.active"
                            class="inner-menu-group"
                            sub-group
                        >
                            <template v-slot:prependIcon>
                                <v-icon
                                    size="18"
                                    class="mr-4 color-icon"
                                    :class="subItem.color ? subItem.color + '--text' : ''"
                                >
                                    {{ subItem.icon }}
                                </v-icon>
                            </template>
                            <template v-slot:appendIcon>
                                <v-icon
                                    class="color-icon"
                                    size="18"
                                >
                                    fa-solid fa-angle-down
                                </v-icon>
                            </template>
                            <template v-slot:activator>
                                <v-list-item-content>
                                    <v-list-item-title
                                        :class="subItem.color ? subItem.color + '--text' : ''"
                                        class="text-wrap"
                                    >
                                        {{ $t(`sideBar.subItems.${subItem.label}`) }}
                                    </v-list-item-title>
                                </v-list-item-content>
                            </template>

                            <template v-for="(innerItem, k) in subItem.subSubItems">
                                <!-- 3rd level as collapsible group when children exist -->
                                <v-list-group
                                    v-if="innerItem.subSubSubItems && innerItem.subSubSubItems.length"
                                    :key="`${k}-c-group`"
                                    v-model="innerItem.active"
                                    sub-group
                                >
                                    <template v-slot:prependIcon>
                                        <v-icon
                                            size="18"
                                            class="color-icon ml-3"
                                            :class="innerItem.color ? innerItem.color + '--text' : ''"
                                        >
                                            {{ innerItem.icon }}
                                        </v-icon>
                                    </template>

                                    <template v-slot:appendIcon>
                                        <v-icon
                                            class="color-icon"
                                            size="18"
                                        >
                                            fa-solid fa-angle-down
                                        </v-icon>
                                    </template>

                                    <template v-slot:activator>
                                        <v-list-item-content class="ml-3">
                                            <v-list-item-title
                                                :class="innerItem.color ? innerItem.color + '--text' : ''"
                                                class="text-wrap"
                                            >
                                                {{ $t(`sideBar.subItems.innerItems.${innerItem.label}`) }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </template>

                                    <!-- actual 3rd-level leaf items -->
                                    <v-list-item
                                        v-for="(thirdItem, z) in innerItem.subSubSubItems"
                                        :key="`${k}-${z}-d`"
                                        :to="thirdItem.to"
                                        class="px-12"
                                        link
                                    >
                                        <v-list-item-icon>
                                            <v-icon
                                                class="color-icon"
                                                small
                                                :class="thirdItem.color ? thirdItem.color + '--text' : ''"
                                            >
                                                {{ thirdItem.icon }}
                                            </v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                            <v-list-item-title
                                                :class="thirdItem.color ? thirdItem.color + '--text' : ''"
                                                class="text-wrap"
                                            >
                                                {{ $t(`sideBar.subItems.innerItems.${thirdItem.label}`) }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list-group>

                                <!-- fallback: plain item when no 3rd level -->
                                <v-list-item
                                    v-else
                                    :key="`${k}-c-item`"
                                    :to="innerItem.to"
                                    class="px-9"
                                    link
                                >
                                    <v-list-item-icon>
                                        <v-icon
                                            class="color-icon"
                                            small
                                            :class="innerItem.color ? innerItem.color + '--text' : ''"
                                        >
                                            {{ innerItem.icon }}
                                        </v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title
                                            :class="innerItem.color ? innerItem.color + '--text' : ''"
                                            class="text-wrap"
                                        >
                                            {{ $t(`sideBar.subItems.innerItems.${innerItem.label}`) }}
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                        </v-list-group>
                        <v-list-item
                            v-else-if="canViewOption(subItem.canView)"
                            :key="`${y}-b-group`"
                            :to="subItem.to"
                            class="px-6"
                            link
                        >
                            <v-list-item-icon>
                                <v-icon
                                    class="color-icon"
                                    small
                                >
                                    {{ subItem.icon }}
                                </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title class="text-wrap sub-items">
                                    {{ $t(`sideBar.subItems.${subItem.label}`) }}
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </template>
                </v-list-group>

                <v-list-item
                    v-else
                    :key="`${i}-a`"
                    :to="navItem.to"
                    link
                >
                    <v-list-item-icon>
                        <v-icon
                            class="color-icon"
                            small
                        >
                            {{ navItem.icon }}
                        </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title class="text-wrap">
                            {{ $t(`sideBar.${navItem.label}`) }}
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </template>
        </v-list>
        <div class="developed_by d-flex justify-center">
            <a
                href="https://draxis.gr/"
                target="_blank"
            >
                {{ $t(`sideBar.footer.developed_by`) }} <br />
                <span>{{ $t(`sideBar.footer.draxis`) }}</span>
            </a>
        </div>
    </v-navigation-drawer>
</template>

<script>
import { mapGetters } from "vuex";
import { getSideNavItems } from "@/helpers/sidenavItems.js";

import CONFIG from "@/common/config";
import stylesConfig from "@/common/stylesConfig.js";

export default {
    name: "Sidebar",

    data() {
        return {
            openDrawer: null,
            navItems: [],
            homeURL: CONFIG.home_page,
            SIDEBAR_WIDTH: stylesConfig.sidebar_width,
        };
    },

    computed: {
        ...mapGetters({
            isOpenDrawer: "globals/getDrawer",
            userRole: "auth/getUserRole",
            userInfo: "auth/getUserInfo",
            wasCompanyUpdated: "globals/getCopmanyUpdate",
        }),
    },

    methods: {
        handleSideNav() {
            this.navItems = getSideNavItems(this.userRole, this.userInfo);
        },

        isMobile() {
            if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                this.openDrawer = false;
                this.$store.dispatch("globals/setDrawer", false);
                return;
            }

            if (window.matchMedia("only screen and (max-width: 900px)").matches) {
                this.openDrawer = false;
                this.$store.dispatch("globals/setDrawer", false);
                return;
            }
            this.openDrawer = true;
            this.$store.dispatch("globals/setDrawer", true);
            return;
        },

        canViewOption(whoCan) {
            let [roleOne, roleTwo = false, roleThree = false] = whoCan.map((item) => item == this.userRole);
            let [all] = whoCan.map((item) => item == "all");

            if (all) {
                return true;
            }

            if (roleOne) {
                return true;
            }
            if (roleTwo) {
                return true;
            }
            if (roleThree) {
                return true;
            }
            return false;
        },
    },

    watch: {
        isOpenDrawer() {
            this.openDrawer = this.isOpenDrawer;
        },

        openDrawer: {
            handler(val) {
                this.$store.dispatch("globals/setDrawer", val);
            },
        },

        wasCompanyUpdated: {
            handler(val) {
                if (val) this.handleSideNav();
            },
        },
    },

    created() {
        this.handleSideNav();
        this.isMobile();
    },
};
</script>

<style lang="scss" scoped>
.side-nav-items {
    text-transform: capitalize;
}
.sub-items {
    font-size: 0.86em;
}

.text-wrap-1 {
    color: red;
}

.nav-title {
    a {
        text-decoration: none;
        color: #fff;
        letter-spacing: 0.05em;
        line-height: 1.4;
    }
}

.nav-divider {
    border-color: #fff;
    border-width: 1px;
}

.developed_by {
    position: absolute;
    bottom: 0;
    left: 0;
    padding: 0;
    z-index: 2000 !important;
    width: 100%;
    a {
        background-color: #304156;
        padding: 0.1em 1.4em;
        font-size: 0.66em;
        font-weight: 400;
        letter-spacing: 0.04em;
        line-height: 1.3;
        color: #bfcbd9;
        text-decoration: none;
        span {
            font-weight: 500;
            font-size: 1em;
        }
    }
}

.new-badge {
    position: absolute !important;
    top: 33% !important;
    right: 15% !important;
    background-color: #0e8714 !important; /* Light green accent-3 */
    color: white !important;
    font-size: 0.75rem !important;
    padding: 2px 6px !important;
    border-radius: 10px !important;
    z-index: 1000 !important;
    text-align: center !important;
}
</style>
